/**
 * Production-safe logging utility
 * In development: logs to console
 * In production: only logs errors, can be extended to send to logging service
 * Respects security mode and admin debug settings
 */

import { isProductionMode } from './security'

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

const isDevelopment = import.meta.env.DEV

interface LogEntry {
  level: LogLevel
  message: string
  data?: any
  timestamp: string
}

class Logger {
  private shouldLog(level: LogLevel): boolean {
    // In production mode, be very restrictive
    if (isProductionMode()) {
      // Only log warnings and errors in production
      return level === 'warn' || level === 'error'
    }
    
    // In development mode, log everything
    if (isDevelopment) {
      return true
    }
    
    // Default to restrictive logging for security
    return level === 'warn' || level === 'error'
  }

  private formatMessage(level: LogLevel, message: string, data?: any): LogEntry {
    return {
      level,
      message,
      data,
      timestamp: new Date().toISOString()
    }
  }

  debug(message: string, ...args: any[]) {
    if (!this.shouldLog('debug')) return
    
    const entry = this.formatMessage('debug', message, args.length > 0 ? args : undefined)
    console.debug(`[DEBUG] ${entry.timestamp} - ${message}`, ...args)
  }

  info(message: string, ...args: any[]) {
    if (!this.shouldLog('info')) return
    
    const entry = this.formatMessage('info', message, args.length > 0 ? args : undefined)
    console.info(`[INFO] ${entry.timestamp} - ${message}`, ...args)
  }

  warn(message: string, ...args: any[]) {
    if (!this.shouldLog('warn')) return
    
    const entry = this.formatMessage('warn', message, args.length > 0 ? args : undefined)
    console.warn(`[WARN] ${entry.timestamp} - ${message}`, ...args)
  }

  error(message: string, error?: Error | any, ...args: any[]) {
    if (!this.shouldLog('error')) return
    
    const entry = this.formatMessage('error', message, { error, args })
    
    if (error instanceof Error) {
      console.error(`[ERROR] ${entry.timestamp} - ${message}`, error, ...args)
    } else {
      console.error(`[ERROR] ${entry.timestamp} - ${message}`, error, ...args)
    }
  }

  // Group logs together (useful for debugging)
  group(label: string) {
    if (isDevelopment) {
      console.group(label)
    }
  }

  groupEnd() {
    if (isDevelopment) {
      console.groupEnd()
    }
  }
}

// Export singleton instance
export const logger = new Logger()

// Export class for testing
export { Logger }

